import {inject, Injectable} from '@angular/core';
import {User} from "../core/classes/user";
import {Observable, of, switchMap} from "rxjs";
import {HttpClient, HttpParams} from "@angular/common/http";
import {catchError, map} from "rxjs/operators";
import {GenUserFeedback} from "../generated/serverModels/GenUserFeedback";
import {UserPreferences} from "../core/classes/user-preferences";
import {ApiKey} from "../core/classes/apiKey";
import {ApiKeyRequest, CreateApiKeyRequest} from "../core/classes/apiKeyRequest";
import { GenApiKeyRequestStatus } from '../generated/serverModels/GenApiKeyRequestStatus';


@Injectable({
  providedIn: 'root',
})
export class UserService {

  private readonly apiUrl = 'api/security/user';
  private http = inject(HttpClient);


  public enableUser(user: User): Observable<User> {
    const url = `${this.apiUrl}/${user.id}/enable`;
    return this.http.put<User>(url, '').pipe(map((res) => new User(res)));
  }

  public disableUser(user: User): Observable<User> {
    const url = `${this.apiUrl}/${user.id}/disable`;
    return this.http.put<User>(url, '').pipe(map((res) => new User(res)));
  }

  public submitFeedback(feedback: GenUserFeedback): Observable<void> {
    const url = 'api/security/user/feedback';
    return this.http.post<void>(url, feedback);
  }


  public sendPasswordResetEmail(username: string): Observable<boolean> {
    const url = 'api/security/user/request-password-reset';
    const params = new HttpParams().set('username', username);
    return this.http.post<boolean>(url, undefined, { params });
  }


  public updateRoles(user: User): Observable<void> {
    const url = 'api/security/user/updateroles';
    return this.http.put<void>(url, user);
  }

  public getUsers(): Observable<User[]> {
    return this.http.get<unknown[]>(this.apiUrl).pipe(
      map((users) => users.map((user) => new User(user as User)))
    );
  }

  public getUserById(id: number): Observable<User> {
    const url = `${this.apiUrl}/by-id/${id}`;

    return this.http.get<User>(url).pipe(
      map(user => new User(user)),
    );
  }

  public approveUser(user: User): Observable<User> {
    const url = `api/security/user/${user.id}/approve`;
    return this.http.put<User>(url, undefined).pipe(
      map(user => new User(user)),
    );
  }

  public denyUser(user: User, reason: string): Observable<void> {
    const url = `api/security/user/${user.id}/deny`;
    const params = new HttpParams()
      .set('reason', reason);
    return this.http.put<void>(url, undefined, { params });
  }

  public updateApiKey(id: number, payload: ApiKey): Observable<ApiKey> {
    const url = `api/apikey/${id}`;
    return this.http.put<ApiKey>(url, payload).pipe(
      map((updatedKey) => {
        return new ApiKey(updatedKey);
      }),
    );
  }

  public requestApiKey(request: CreateApiKeyRequest): Observable<ApiKeyRequest> {
    const url = 'api/apikey/request';
    return this.http.post<ApiKeyRequest>(url, request).pipe(
      map(response => new ApiKeyRequest(response)),
    );
  }

  public getApiKeyRequests(status?: GenApiKeyRequestStatus): Observable<ApiKeyRequest[]> {
    const url = `api/apikey/request`;
    const params = status ? new HttpParams().set('status', status.toString()) : undefined;
    return this.http.get<unknown[]>(url, { params }).pipe(
      map((requests) => requests.map((request) => new ApiKeyRequest(request as ApiKeyRequest)))
    );
  }

  public approveApiKeyRequest(requestId: number): Observable<ApiKeyRequest> {
    const url = `api/apikey/request/${requestId}/approve`;
    return this.http.post<unknown>(url, undefined).pipe(
      map((request) => new ApiKeyRequest(request as ApiKeyRequest))
    );
  }

  public denyApiKeyRequest(requestId: number, deniedReason: string): Observable<ApiKeyRequest> {
    const url = `api/apikey/request/${requestId}/deny`;
    return this.http.post<unknown>(url, deniedReason).pipe(
      map((request) => new ApiKeyRequest(request as ApiKeyRequest))
    );
  }

  public getUserLayerAccess(userId: number): Observable<number[]> {
    const url = `${this.apiUrl}/${userId}/layer-access`;
    return this.http.get<number[]>(url);
  }

  public updateUserLayerAccess(userId: number, layerIds: number[]): Observable<void> {
    const url = `${this.apiUrl}/${userId}/layer-access`;
    return this.http.put<void>(url, layerIds);
  }

  public getUserReportAccess(userId: number): Observable<number[]> {
    const url = `${this.apiUrl}/${userId}/report-access`;
    return this.http.get<number[]>(url);
  }

  public updateUserReportAccess(userId: number, reportIds: number[]): Observable<void> {
    const url = `${this.apiUrl}/${userId}/report-access`;
    return this.http.put<void>(url, reportIds);
  }
}
